import React from 'react';
import { IconButton, Dialog, DialogTitle, DialogContent } from '@mui/material';
import { Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CloseIcon from '@mui/icons-material/Close';
import { Clipboard } from '@capacitor/clipboard';
import { SingleMeeting } from './Races';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import { Finisher, Meeting, serverRequest, serverRequestBlob, formatDate, MONTHS, ETYPES, ETYPES_DESCR,
     formatDateString, eventIconText, ISNATIVE, sortKeys, 
     getRaceDeepLink} from './Util';
import { SCREEN } from './Widgets';

function arrayEquals<T>(a?: T[], b?: T[]) {
    return Array.isArray(a) && Array.isArray(b) &&
            a.length === b.length &&
            a.every((val, index) => val === b[index]);
}

function getGroupName(date: Date, event: string, groupby: string): string {
    const is_parkrun = (event === "parkrun");
    switch (groupby) {
        case 'W': // removed weekly grouping (we group parkruns by date anyway)
        case 'M':
            if (is_parkrun)
                return "parkrun " + formatDate(date, false);
            return MONTHS[date.getMonth()] + " " + date.getFullYear();
        case 'Y':
            return date.getFullYear().toFixed(0);
        case 'D':
        default:
            if (is_parkrun)
                return "parkrun " + formatDate(date, false);
            return formatDate(date);
    }
}

// TBD: adapt to screen size, and use two columns on very large screens
function getMeetingCard(key: string, race: Meeting, isOpen: boolean, isNested: boolean,
                        onSelectRace: React.MouseEventHandler<HTMLDivElement>, screenSize: number) {
    var [event_icon, event_text] = eventIconText(race.Event, race.EType);
    var datestr = formatDateString(race.Date);

    var classes = isOpen
        ? 'gridcard cardselected'
        : (isNested ? 'gridcard card-inner' : 'gridcard card');

    return  <div className={classes} style={{ gridTemplateColumns: '15fr 75fr 10fr'}} key={key} onClick={onSelectRace} data-key={race.MeetingID}>
        <div style={{gridColumn: '1', gridRow: '1'}} >{event_icon}</div>
        <div style={{gridColumn: '1', gridRow: '2'}} >{event_text}</div>
        <div className='row-stat' style={{gridColumn: '3', gridRow: '1'}}>{race.Num}<DirectionsRunIcon /></div>
        <div className='row-title' style={{gridColumn: '2', gridRow: '1'}}>{race.Meeting}</div>
        <div className='row-meta' style={{gridColumn: '2', gridRow: '2'}}>{datestr}{' - '}{race.Venue}</div>
        <div className='chevron-muted' style={{gridColumn: '3', gridRow: '2'}}><OpenInNewIcon fontSize="large" /></div>
    </div>
}

interface MeetingGroupProps {
    datestr: string;
    nraces: number;
    nrunners: number;
    nevents: number[];
    // awards;
}

function MeetingGroup(props: React.PropsWithChildren<MeetingGroupProps>) {
    var events = '';
    for (var n=0; n<props.nevents.length; n++) {
        if (props.nevents[n] > 0)
            events += props.nevents[n] + " " + ETYPES_DESCR[n] + " | "
    }
    if (events.length > 3)
        events = events.substr(0, events.length-3);

    const is_parkrun = props.datestr.startsWith("parkrun");
    const border = is_parkrun
        ? '3px solid rgba(46,125,50,0.4)'
        : '3px solid rgba(245,158,11,0.5)'
    const title = is_parkrun ? props.datestr.substring(7) : props.datestr;
    const icon = is_parkrun ? eventIconText("parkrun", "PR")[0] : eventIconText("", "");

    return <Accordion sx={{ borderLeft: border, marginBottom: '6px' }} >
            <AccordionSummary expandIcon={<ExpandMoreIcon fontSize='large' /> }> 
                <div style={{ display: 'grid', gridTemplateColumns: '10fr 30fr 45fr 15fr', color: 'var(--title-color)', gridGap: '5px', marginRight: '10px', width: '100%'}}>
                    <div style={{gridColumn: '1', gridRow: '1', fontSize: '1.6rem', paddingRight: '2px'}} >
                        {icon}
                    </div>
                    <div style={{gridColumn: '2', gridRow: '1', fontSize: '1.6rem', paddingRight: '5px'}} >    
                        {title}
                    </div>
                    <div className='row-meta' style={{ gridColumn: '3', gridRow: '1' }}>
                        {events}
                    </div>
                    <div className='row-meta' style={{ gridColumn: '4', gridRow: '1' }}>
                        {props.nrunners}<DirectionsRunIcon width='16px' height='16px' />
                    </div>
                </div>
            </AccordionSummary> 
            <AccordionDetails sx={{ p: 0, borderTop: '1px solid var(--primary-color)' }} >
                {props.children}
            </AccordionDetails>
        </Accordion>
}

interface MeetingsProps {
    races: Record<string, Meeting>;
    start_grouped: boolean;
    group_by: string;
    loading: boolean;
    selRaceID?: number;
    onSelectRace?: (raceID: number) => void;
}

interface MeetingsState {
    selRaceID: number;
    is_sparse: boolean;
}

export class Meetings extends React.Component<MeetingsProps, MeetingsState> {
    constructor(props: MeetingsProps) {
        super(props);
        this.state = { selRaceID: 0, is_sparse: false };
        this.onSelectRace = this.onSelectRace.bind(this);
    }

    componentDidMount() {
        this.recalc();
    }

    componentDidUpdate(prevProps: MeetingsProps) {
        if (! arrayEquals(Object.keys(prevProps.races), Object.keys(this.props.races)))
            this.recalc();
    }

    // Calculates the number of groups, given the group_by prop
    // and decides if the table is sparse (so will be displayed without grouping)
    recalc() {
        const racekeys = Object.keys(this.props.races);
        let ngroups = 0;
        let is_sparse = true;
        if (racekeys.length > 10) { // small lists shown without groups
            const groups = new Set<string>();
            for (let n = 0; n < racekeys.length; n++) {
                const key = racekeys[n];
                const race: Meeting = this.props.races[key];
                const group = getGroupName(new Date(race.Date), race.Event, this.props.group_by);
                groups.add(group);
            }

            ngroups = groups.size;
            is_sparse = (ngroups * 2) > racekeys.length;
        }
        this.setState( { is_sparse: is_sparse } );
    }
    
    onSelectRace(event: React.MouseEvent<HTMLDivElement>) {
        const ID = event.currentTarget.dataset.key;
        if (!ID) return;

        const raceID = Number(ID);

        if (this.props.onSelectRace) {
            this.props.onSelectRace(raceID);
        } else {
            this.setState({
                selRaceID: this.state.selRaceID === raceID ? 0 : raceID
            });
        }
    }

    getCounters() {
        var races = this.props.races;
        var racekeys = Object.keys(races);
        var nraces = 0, nrunners = 0, numpb = 0, numsb = 0, numtop10 = 0, numytop10 = 0;
        var num_et: number[] = new Array(ETYPES.length).fill(0);
        for (var n=0; n<racekeys.length; n++) {
            var key = racekeys[n];
            var race: Meeting = races[key];
            nraces += 1;
            nrunners += race.Num;
            numpb += race.NumPB;
            numsb += race.NumSB;
            numtop10 += race.NumTop10;
            numytop10 += race.NumYTop10;
            var et_ind = ETYPES.indexOf(race.EType);
            if (et_ind >= 0)
                num_et[et_ind] += 1;
        }

        return [nraces, nrunners, numpb, numsb, numtop10, numytop10, num_et]
    }

    render() {
        if (this.props.loading) return <h2>Loading...</h2>;

        const ScreenWidth = SCREEN.SMALL;

        const activeRaceID = this.props.selRaceID !== undefined
                                ? this.props.selRaceID
                                : this.state.selRaceID;

        const races = this.props.races;
        let racekeys = Object.keys(races);
        racekeys = sortKeys(races, [
            { key: 'Date', asc: false },
            { key: 'Num', asc: false },
            { key: 'Meeting', asc: true },
        ]);

        if (racekeys.length === 0) return <h2>No events match your selection</h2>;

        // If sparse, just render cards in sorted order
        if (this.state.is_sparse) {
            const cards = racekeys.map((key) => {
                const race: Meeting = races[key];
                return getMeetingCard(
                    key,
                    race,
                    race.MeetingID === activeRaceID,
                    false,
                    this.onSelectRace,
                    ScreenWidth
                );
            });
            return <div className="cardset">{cards}</div>;
        }

        // Not sparse => group, but DO NOT assume contiguous groups
        type GroupAcc = {
            cards: JSX.Element[];
            nraces: number;
            nrunners: number;
            numpb: number;
            numsb: number;
            numtop10: number;
            numytop10: number;
            num_et: number[];
            // for ordering: keep the newest date seen in the group
            maxDate: Date;
        };

        const groupMap = new Map<string, GroupAcc>();
        const groupOrder: string[] = []; // first-seen order (we can sort later)

        for (let n = 0; n < racekeys.length; n++) {
            const key = racekeys[n];
            const race: Meeting = races[key];

            const groupName = getGroupName(new Date(race.Date), race.Event, this.props.group_by);
            const raceDate = new Date(race.Date);

            let acc = groupMap.get(groupName);
            if (!acc) {
                acc = {
                    cards: [],
                    nraces: 0,
                    nrunners: 0,
                    numpb: 0,
                    numsb: 0,
                    numtop10: 0,
                    numytop10: 0,
                    num_et: new Array(ETYPES.length).fill(0),
                    maxDate: raceDate,
                };
                groupMap.set(groupName, acc);
                groupOrder.push(groupName);
            }

            // update ordering anchor
            if (raceDate > acc.maxDate) acc.maxDate = raceDate;

            // stats
            acc.nraces += 1;
            acc.nrunners += race.Num;
            acc.numpb += race.NumPB;
            acc.numsb += race.NumSB;
            acc.numtop10 += race.NumTop10;
            acc.numytop10 += race.NumYTop10;

            const et_ind = ETYPES.indexOf(race.EType);
            if (et_ind >= 0) acc.num_et[et_ind] += 1;

            // card
            acc.cards.push(
                getMeetingCard(
                    key,
                    race,
                    race.MeetingID === activeRaceID,
                    true,
                    this.onSelectRace,
                    ScreenWidth
                )
            );
        }

        // Sort groups newest-first (matches your overall Date desc intent)
        groupOrder.sort((a, b) => {
            const da = groupMap.get(a)!.maxDate.getTime();
            const db = groupMap.get(b)!.maxDate.getTime();
            return db - da;
        });

        const groups = groupOrder.map((gname) => {
            const acc = groupMap.get(gname)!;
            return (
            <MeetingGroup
                key={gname}
                datestr={gname}
                nraces={acc.nraces}
                nrunners={acc.nrunners}
                nevents={acc.num_et}
                // awards={formatAwards(acc.numtop10, acc.numytop10, acc.numpb, acc.numsb, false)}
            >
                {acc.cards}
            </MeetingGroup>
            );
        });

        return <div>{groups}</div>;
    }
}

interface MeetingFinishersState {
    race: Record<string, Finisher>;
}

interface MeetingFinishersProps {
    raceID: number;
    runnerID: string;
    showDescr: boolean;
}

export class MeetingFinishers extends React.Component<MeetingFinishersProps, MeetingFinishersState> {
    constructor(props: MeetingFinishersProps) {
        super(props);
        this.state = {race: {} };
    }

    private lastRequestedRaceID: number | null = null;
    private raceReqSeq = 0;

    componentDidMount() {
        this.loadRaceIfNeeded(this.props.raceID);
    }

    componentDidUpdate(prevProps: MeetingFinishersProps) {
        if (prevProps.raceID !== this.props.raceID)
            this.loadRaceIfNeeded(this.props.raceID);
    }

    loadRaceIfNeeded(id: number) {
        if (this.lastRequestedRaceID === id) return;
            this.lastRequestedRaceID = id;
        const seq = ++this.raceReqSeq;

        serverRequest("meeting?meetID=" + id).then(res => {
            if (seq !== this.raceReqSeq) return;
            this.setState({ race: res });
        });
    }

    render() {
        return <SingleMeeting meetingID={this.props.raceID} finishers={this.state.race} showDescr={this.props.showDescr} selRunnerID={this.props.runnerID} />
    }
}

interface MeetingListProps {
    filter: string;
    start_grouped: boolean;
    group_by: string;  // '', 'D', 'W', 'M', 'Y' - none, day, week, month, year
    print: boolean;
    selRaceID?: number;
    onSelectRace?: (raceID: number) => void;
}

interface MeetingListState {
    races: {};
    loading: boolean;
}

export class MeetingList extends React.Component<MeetingListProps, MeetingListState> {
    constructor(props: MeetingListProps) {
        super(props);
        this.state = { races: {}, loading: false };
        this.onPrint = this.onPrint.bind(this);
    }

    private lastRequestedFilter: string | null = null;
    private requestSeq = 0;

    componentDidMount() {
        this.loadRacesIfNeeded();
    }

    componentDidUpdate(prevProps: any) {
        if (prevProps.filter !== this.props.filter) {
            this.loadRacesIfNeeded();
        }
    }

    loadRacesIfNeeded() {
        const filter = String(this.props.filter);

        // Dedupe: if we already requested this exact filter, do nothing
        if (this.lastRequestedFilter === filter) return;
        this.lastRequestedFilter = filter;

        const seq = ++this.requestSeq;

        this.setState({ loading: true });

        serverRequest("race?" + filter).then(res => {
            // Ignore stale responses if a newer request started
            if (seq !== this.requestSeq) return;
            this.setState({ races: res, loading: false });
        });
    }

    onPrint() {
        serverRequestBlob("printresults?" + this.props.filter)
        .then(blob => {
            var url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            const fname = "Non-Parkrun Results.pdf";
            link.href = url;
            link.setAttribute('download', fname);
            link.click();
        });
    }

    render() {
        var list = <Meetings 
                        races={this.state.races} 
                        start_grouped={this.props.start_grouped} 
                        group_by={this.props.group_by} 
                        loading={this.state.loading}
                        selRaceID={this.props.selRaceID}
                        onSelectRace={this.props.onSelectRace}
                    />

        var print = this.props.print && ISNATIVE ?
                    <IconButton onClick={this.onPrint} >
                        <FileDownloadOutlinedIcon fontSize='large'/>
                    </IconButton> :
                    <></>;

        return (
          <div>
            {print}
            {list}
        </div>)
    }
}

interface MeetingListWithPopupProps {
    filter: string;
    start_grouped: boolean;
    group_by: string;
    print: boolean;
    title?: string;
    deepLinkBase?: string;              // e.g. '/races'
    startRaceID?: string;
    navigateTo?: (path: string, replace?: boolean) => void;
}

interface MeetingListWithPopupState {
    selRaceID: number;
}

export class MeetingListWithPopup extends React.Component<MeetingListWithPopupProps, MeetingListWithPopupState> {
    constructor(props: MeetingListWithPopupProps) {
        super(props);

        const startID =
            this.props.startRaceID && !Number.isNaN(Number(this.props.startRaceID))
                ? Number(this.props.startRaceID)
                : 0;

        this.state = { selRaceID: startID };

        this.onSelectRaceID = this.onSelectRaceID.bind(this);
        this.closeRacePopup = this.closeRacePopup.bind(this);
    }

    componentDidUpdate(prevProps: MeetingListWithPopupProps) {
        if (prevProps.startRaceID !== this.props.startRaceID) {
            const nextID =
                this.props.startRaceID && !Number.isNaN(Number(this.props.startRaceID))
                    ? Number(this.props.startRaceID)
                    : 0;

            if (nextID !== this.state.selRaceID) {
                this.setState({ selRaceID: nextID });
            }
        }
    }

    onSelectRaceID(raceID: number) {
        const nextID = this.state.selRaceID === raceID ? 0 : raceID;
        const base = this.props.deepLinkBase ?? '/races';

        this.setState({ selRaceID: nextID }, () => {
            if (this.props.navigateTo) {
                if (nextID > 0)
                    this.props.navigateTo(`${base}/${nextID}`);
                else
                    this.props.navigateTo(base);
            }
        });
    }

    closeRacePopup() {
        const base = this.props.deepLinkBase ?? '/races';

        this.setState({ selRaceID: 0 }, () => {
            if (this.props.navigateTo)
                this.props.navigateTo(base);
        });
    }

    render() {
        return (
            <>
                <MeetingList
                    key={this.props.filter}
                    filter={this.props.filter}
                    start_grouped={this.props.start_grouped}
                    group_by={this.props.group_by}
                    print={this.props.print}
                    selRaceID={this.state.selRaceID}
                    onSelectRace={this.onSelectRaceID}
                />

                <MeetingFinishersPopup
                    raceID={this.state.selRaceID}
                    runnerID=""
                    deepLinkBase={this.props.deepLinkBase}
                    onClose={this.closeRacePopup}
                />
            </>
        );
    }
}

interface MeetingFinishersPopupProps {
    raceID: number;
    runnerID?: string;
    deepLinkBase?: string;
    onClose: () => void;
}

export class MeetingFinishersPopup extends React.Component<MeetingFinishersPopupProps> {
    async copyRaceLink() {
        if (!this.props.raceID) return;
        const url = getRaceDeepLink(this.props.raceID);

        try {
            await Clipboard.write({ string: url });
        } catch {
            await navigator.clipboard.writeText(url);
        }
    }

    render() {
        return (
            <Dialog
                open={this.props.raceID !== 0}
                onClose={this.props.onClose}
                fullWidth
                maxWidth={false}
                className="dialog"
            >
                <DialogTitle className="dialog-title">
                    <span>{'Race results'}</span>

                    <div style={{ marginLeft: 'auto', display: 'flex', gap: '4px' }}>
                        <IconButton
                            onClick={() => this.copyRaceLink()}
                            aria-label="Copy race link"
                        >
                            <ContentCopyIcon />
                        </IconButton>

                        <IconButton
                            onClick={this.props.onClose}
                            aria-label="Close"
                            className="dialog-close"
                        >
                            <CloseIcon />
                        </IconButton>
                    </div>
                </DialogTitle>

                <DialogContent dividers className="dialog-content">
                    {this.props.raceID !== 0 && (
                        <MeetingFinishers
                            raceID={this.props.raceID}
                            runnerID={this.props.runnerID ?? ''}
                            showDescr={true}
                        />
                    )}
                </DialogContent>
            </Dialog>
        );
    }
}
